<template>
    <div class="oauth-callback">
        <div class="oauth-callback__card">
            <div class="oauth-callback__loading" v-if="loading">
                <img src="@/assets/images/google-logo.svg" alt="Google" class="oauth-callback__logo" />
                <div class="oauth-callback__dots">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
                <p class="oauth-callback__text">{{ $t('Completing sign in...') }}</p>
            </div>
            <div class="oauth-callback__error" v-else-if="error">
                <div class="oauth-callback__error-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                        <path d="M12 7v6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        <circle cx="12" cy="16" r="1" fill="currentColor"/>
                    </svg>
                </div>
                <h2 class="oauth-callback__error-title">{{ $t('Sign in failed') }}</h2>
                <p class="oauth-callback__error-text">{{ error }}</p>
                <ui-button :to="loginRoute">
                    {{ $t('Back to Login') }}
                </ui-button>
            </div>
        </div>
    </div>
</template>

<script>
import socialLoginMixin from '@/plugins/mixin/social-login.mixin';
import UiButton from '@/components/ui/buttons/Button.vue';

export default {
    name: 'GoogleCallbackPage',
    components: { UiButton },
    mixins: [socialLoginMixin],
    data() {
        return {
            loading: true,
            error: null,
            user: {
                _tk: null,
                name: 'google',
                siteId: null,
                redirectUrl: null,
            }
        };
    },
    computed: {
        loginRoute() {
            const query = { ...this.$route.query };
            const google_params = ['code', 'state', 'scope', 'authuser', 'prompt', 'error'];
            google_params.forEach(param => delete query[param]);
            return { path: '/login', query };
        }
    },
    beforeMount() {
        const { state } = this.$route.query;
        if (state) {
            try {
                const stateData = JSON.parse(atob(state));
                const stateParams = { ...stateData };
                delete stateParams.nonce;
                this.$router.replace({ query: { ...this.$route.query, ...stateParams } });
            } catch (error) {
                this.showError(error);
            }
        }
    },
    async mounted() {
        await this.handleCallback();
    },
    methods: {
        async handleCallback() {
            try {
                const { code, state, error: oauthError } = this.$route.query;

                if (oauthError) {
                    this.error = this.$t('Google sign in was cancelled or failed');
                    this.loading = false;
                    return;
                }

                if (!code) {
                    this.error = this.$t('Invalid callback response');
                    this.loading = false;
                    return;
                }

                const savedState = sessionStorage.getItem('google_oauth_state');
                if (!savedState || savedState !== state) {
                    this.error = this.$t('Invalid state parameter');
                    this.loading = false;
                    return;
                }

                const stateData = JSON.parse(atob(state));
                sessionStorage.removeItem('google_oauth_state');

                this.user.siteId = stateData.site_id;
                this.user.redirectUrl = stateData.redirect_url;

                const { data: tokenData } = await this.axiosAccount.post('/oauth/google/callback', {
                    code: code,
                    redirect_uri: `${globalThis.location.origin}/oauth/google/callback`
                });

                this.user._tk = tokenData._tk;
                await this.socialNetWorkSucess(this.user);

            } catch (err) {
                this.error = err.response?.data?.message || this.$t('An error occurred during sign in');
                this.loading = false;
            }
        },
        showError(error) {
            this.error = error.response?.data?.message || this.$t('An error occurred during sign in');
            this.loading = false;
        }
    }
};
</script>

<style lang="scss" scoped>
.oauth-callback {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;

    &__card {
        background-color: #ffffff;
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        padding: 48px 40px;
        text-align: center;
        max-width: 400px;
        width: 100%;
    }

    &__loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
    }

    &__logo {
        width: 48px;
        height: 48px;
    }

    &__dots {
        display: flex;
        gap: 6px;

        span {
            width: 8px;
            height: 8px;
            background-color: #dadce0;
            border-radius: 50%;
            animation: bounce 1.4s ease-in-out infinite;

            &:nth-child(1) {
                animation-delay: 0s;
            }

            &:nth-child(2) {
                animation-delay: 0.16s;
            }

            &:nth-child(3) {
                animation-delay: 0.32s;
            }
        }
    }

    &__text {
        color: #5f6368;
        font-size: 15px;
        font-weight: 400;
        margin: 0;
    }

    &__error {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
    }

    &__error-icon {
        width: 56px;
        height: 56px;
        color: #d93025;
    }

    &__error-title {
        color: #202124;
        font-size: 20px;
        font-weight: 600;
        margin: 0;
    }

    &__error-text {
        color: #5f6368;
        font-size: 14px;
        margin: 0;
        line-height: 1.5;
    }
}

@keyframes bounce {
    0%, 80%, 100% {
        transform: scale(0.6);
        background-color: #dadce0;
    }
    40% {
        transform: scale(1);
        background-color: var(--color-accent-base, #4285f4);
    }
}
</style>

